import { create } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'

import type { CreateFieldTripInput } from '@/api/field-trips'
import type { SaveBackUpStandingOrderInput } from '@/api/school-back-up-meals'
import type { SupplyCartLine } from '@/lib/school-orders/supplies-cart'

export interface ServiceReviewLine {
  name: string
  quantity: number
  unitPriceCents: number | null
  detail?: string
  mealtime?: string
}
interface BaseEntry {
  id: string
  locationId: string
  locationName: string
  title: string
  lines: ServiceReviewLine[]
}
export type SchoolServiceCartEntry = BaseEntry &
  (
    | { kind: 'bulk'; supplyLines: SupplyCartLine[]; notes: string }
    | { kind: 'field-trip'; input: CreateFieldTripInput }
    | { kind: 'standing'; input: SaveBackUpStandingOrderInput }
  )

/** Standing orders charge when meals materialize, never when the schedule is saved. */
export function serviceCartDebit(entry: SchoolServiceCartEntry): number | null {
  if (entry.kind === 'standing') {
    return 0
  }
  if (entry.lines.some((line) => line.unitPriceCents === null)) {
    return null
  }
  return entry.lines.reduce((sum, line) => sum + line.quantity * (line.unitPriceCents ?? 0), 0)
}

interface ServiceCartState {
  entries: Record<string, SchoolServiceCartEntry>
  put: (entry: SchoolServiceCartEntry) => void
  remove: (id: string) => void
}
export const useSchoolServiceCart = create<ServiceCartState>()(
  persist(
    (set) => ({
      entries: {},
      put: (entry) => set((state) => ({ entries: { ...state.entries, [entry.id]: entry } })),
      remove: (id) =>
        set((state) => {
          const entries = { ...state.entries }
          delete entries[id]
          return { entries }
        }),
    }),
    { name: 'ordo.school-service-cart', storage: createJSONStorage(() => sessionStorage) },
  ),
)

export function serviceCheckoutUrl(locationId: string): string {
  return `/orders/school/checkout?serviceLocationId=${encodeURIComponent(locationId)}`
}
